// FindExistingSection.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getNodeTypeLabelFromDefinition } from 'utilities/NodeTypeLabels';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';

// CSS
import './FindExistingSection.css';

// Icons
import Icon from 'svg/Icon';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * FindExistingSection
 * 
 * Renders the UI for finding and attaching existing nodes in the Add Modal.
 * 
 * @param {string} searchTerm - The current search term entered by the user.
 * @param {function} setSearchTerm - Function to update the search term state.
 * @param {string} searchError - Any error message resulting from the search operation.
 * @param {boolean} searching - Indicates whether a search operation is currently in progress.
 * @param {boolean} noResults - Indicates whether the search returned no results.
 * @param {Array} searchResults - The array of nodes that match the search criteria.
 * @param {Map} availableTypeMap - A map of type keys to type definitions for displaying type information in results.
 * @param {string} anchorNodeKey - The key of the anchor node to which an existing node may be attached.
 * @param {string} attachingNodeKey - The key of the node currently being attached, used for UI feedback.
 * @param {function} onAttachExisting - Handler function to call when an existing node is selected for attachment.
 * @param {boolean} enableSelectionWithoutAnchor - Enables selection even when no anchor node is provided.
 * @param {string[]} [selectedNodeKeys] - Optional keys shown as selected (multi-select UIs).
 * 
 * @returns {ReactNode} The rendered component.
 */
const FindExistingSection = React.memo(({
  searchTerm,
  setSearchTerm,
  searchError,
  searching,
  noResults,
  searchResults,
  availableTypeMap,
  anchorNodeKey,
  attachingNodeKey,
  onAttachExisting,
  hideInput = false,
  enableSelectionWithoutAnchor = false,
  selectedNodeKeys = null,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [hoveredResultKey, setHoveredResultKey] = React.useState('');

  // ----- RENDER ----- //

  return (

    <div className="find-existing-section-container">

      {/* CONTROLS */}
      {!hideInput && (

        <div className="find-existing-section-controls">
          <TextInput
            value={searchTerm}
            placeholder="Search existing..."
            autoFocus
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
      )}

      {/* HELP TEXT */}
      {searchError && (
        <div
          className="find-existing-section-help-text"
        >
          {searchError}
        </div>
      )}

      {/* HELP TEXT */}
      {searching && (
        <div
          className="find-existing-section-help-text"
        >
          Searching...
        </div>
      )}

      {/* HELP TEXT */}
      {noResults && (
        <div
          className="find-existing-section-help-text"
        >
          No results found.
        </div>
      )}

      {/* SEARCH RESULTS */}
      <div className="find-existing-section-search-results">

        {(searchResults || []).map((result) => {

          // Type definition for the result
          const typeDef = result?.type ? availableTypeMap.get(result.type) : null;

          // Label to display for the type of the result
          const rowTypeLabel = getNodeTypeLabelFromDefinition(typeDef, result?.type, { fallback: 'Node' });

          // Prefer connector/source logo when the result is sourced
          const {
            iconKey: resolvedIconKey,
            iconPath: resolvedIconPath,
            iconResource: resolvedIconResource,
          } = resolveNodeIcon({ node: result, typeDef });
          const fallbackIconKey = resolvedIconKey || (
            !resolvedIconPath && !resolvedIconResource ? 'deployed_code' : ''
          );

          // Determine if the search result should be disabled (not clickable) based on whether 
          // it is the anchor node, or if it is currently being attached
          const isDisabled = (!enableSelectionWithoutAnchor && !anchorNodeKey)
            || result?.key === anchorNodeKey
            || attachingNodeKey === result?.key;
          const resultKey = String(result?.key || '');
          const isHovered = hoveredResultKey === resultKey;
          const isSelected = Array.isArray(selectedNodeKeys)
            && selectedNodeKeys.includes(resultKey);

          return (

            <div
              className={`find-existing-section-search-row${isSelected ? ' is-selected' : ''}`}
              style={{
                backgroundColor: isSelected
                  ? theme.backgroundHover
                  : (!isDisabled && isHovered ? theme.backgroundHover : 'transparent'),
                borderColor: isSelected
                  ? theme.foregroundMuted
                  : (!isDisabled && isHovered ? theme.outline : theme.backgroundMuted),
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.6 : 1,
              }}
              key={result?.key}
              onClick={() => {
                if (!isDisabled) onAttachExisting(result);
              }}
              onMouseEnter={() => {
                if (!isDisabled) setHoveredResultKey(resultKey);
              }}
              onMouseLeave={() => setHoveredResultKey('')}
            >
              {/* ICON */}
              <div className="find-existing-section-search-row-icon">
                {resolvedIconResource ? (
                  <img
                    alt=""
                    className="find-existing-section-search-row-logo"
                    draggable={false}
                    src={resolvedIconResource}
                    width={24}
                    height={24}
                  />
                ) : fallbackIconKey ? (
                  <Icon
                    iconKey={fallbackIconKey}
                    width={24}
                    height={24}
                    color={theme.foregroundMuted}
                  />
                ) : (
                  <Icon
                    path={resolvedIconPath}
                    width={24}
                    height={24}
                    color={theme.foregroundMuted}
                  />
                )}
              </div>

              {/* TEXT */}
              <div className="find-existing-section-search-row-text">

                {/* TITLE */}
                <div
                  className="find-existing-section-search-row-title"
                >
                  {result?.title || 'Untitled'}
                </div>

                {/* TYPE */}
                <div
                  className="find-existing-section-search-row-type"
                >
                  {rowTypeLabel}
                </div>

              </div>

            </div>

          );

        })}

      </div>

    </div>

  );
});

// Dev Tools
FindExistingSection.displayName = 'FindExistingSection';

export default FindExistingSection;
